import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { endOfToday } from "date-fns/esm";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { FormattedMessage, useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import ActivityTypeWithCount from "@mapmycustomers/shared/types/entity/activities/ActivityTypeWithCount";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import DashCard from "@app/component/DashCard";
import CardCta from "@app/component/DashCard/CardCta";
import SelectField from "@app/component/input/SelectField";
import MoreButton from "@app/component/MoreButton";
import SelectActivityTypesModal from "@app/component/SelectEntityDialog/SelectActivityTypesModal";
import { SIMPLE_DATE_RANGE_LABELS } from "@app/scene/home/component/Cards/util";
import { getActitivityTypesWithCount, isActivityByTypesDataLoading } from "@app/scene/home/store";
import { fetchActivityByTypesData } from "@app/scene/home/store/actions";
import getStartDate from "@app/scene/home/util/getStartDate";
import DEFAULT_CHART_CONFIG from "@app/scene/reports/components/Activity/components/ActivityTypes/default-chart-config";
import RecordListByActivityTypeModal from "@app/scene/reports/components/Activity/components/ActivityTypes/RecordListByActivityTypeModal";
import useActivityTypesChartConfig from "@app/scene/reports/components/Activity/components/ActivityTypes/useActivityTypesChartConfig";
import analytisc from "@app/scene/reports/reportsAnalytics";
import { getActivityTypes } from "@app/store/activity";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import defaultIdGetter from "@app/util/defaultIdGetter";
import defaultNodeNameGetter from "@app/util/defaultNodeNameGetter";
import { formatDate } from "@app/util/formatters";

import styles from "./ActivityTypes.module.scss";

const chartStyles = {
  width: "100%",
};

interface Props {
  activityTypes: ActivityType[];
  activityTypesWithCount: ActivityTypeWithCount[];
  className?: string;
  fetchActivityByTypesData: typeof fetchActivityByTypesData.request;
  hasActivities: boolean;
  isLoading: boolean;
  isReadOnlyMember: boolean;
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal;
  selectedUsers: User[];
}

const ActivityTypesCard: React.FC<Props> = ({
  activityTypes,
  activityTypesWithCount,
  className,
  fetchActivityByTypesData,
  hasActivities,
  isLoading,
  isReadOnlyMember,
  onShowCreateActivityModal,
  selectedUsers,
}) => {
  const intl = useIntl();

  const [selectedActivityTypes, setActivityTypes] = useState<ActivityType[]>(activityTypes);
  const [activityListModalVisible, showActivityListModal, hideActivityListModal] = useBoolean();
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | undefined>();
  const [dateRange, setDateRange] = useState(
    SIMPLE_DATE_RANGE_LABELS.length ? SIMPLE_DATE_RANGE_LABELS[0].value : ""
  );

  const startDate = getStartDate(dateRange);
  const handleChangeDateRange = useCallback(
    (value) => {
      setDateRange(`${value}`);
      analytisc.clicked(["Activities by Type", "Date Range"], { range: value });
    },
    [setDateRange]
  );

  const handleChangeType = useCallback(
    (activityType: ActivityType) => {
      showActivityListModal();
      setSelectedActivityType(activityType);
    },
    [setSelectedActivityType, showActivityListModal]
  );

  useChangeTracking(
    () => analytisc.clicked(["Activities by Type", "Select Users"], { users: selectedUsers }),
    [selectedUsers]
  );

  useChangeTracking(
    () =>
      analytisc.clicked(["Activities by Type", "Select Activity types"], {
        types: selectedActivityTypes,
      }),
    [selectedActivityTypes]
  );

  const handleAddActivity = useCallback(() => {
    onShowCreateActivityModal(undefined);
    analytisc.clicked(["Activities by Type", "Add Activity"]);
  }, [onShowCreateActivityModal]);

  const [activityTypesDialogVisible, showActivityTypesDialog, hideActivityTypesDialog] =
    useBoolean();

  const title = useMemo(() => {
    return (
      <>
        <h2>
          {intl.formatMessage(
            {
              id: "activityListModal.title",
              defaultMessage: "Done activities by type: “{activityType}”",
              description: "Records by source list modal title",
            },
            {
              activityType: selectedActivityType?.name,
            }
          )}
        </h2>
        <h6>{startDate && `${formatDate(startDate, "PP")} — ${formatDate(endOfToday(), "PP")}`}</h6>
      </>
    );
  }, [intl, selectedActivityType, startDate]);

  useEffect(() => {
    fetchActivityByTypesData({
      assigneeIds: selectedUsers.map(({ id }) => id),
      crmActivityTypeIds: selectedActivityTypes.map(({ id }) => id),
      dateRange,
    });
  }, [fetchActivityByTypesData, dateRange, selectedUsers, selectedActivityTypes]);

  const hasFilteredActivities = activityTypesWithCount.some(({ count }) => count > 0);

  const options = useActivityTypesChartConfig(
    activityTypesWithCount,
    DEFAULT_CHART_CONFIG,
    handleChangeType
  );

  return (
    <DashCard
      actions={[
        <SelectField<string>
          key="date-range"
          label={intl.formatMessage({
            id: "home.cards.activityTypes.selectField.label",
            defaultMessage: "Date Range",
            description: "Label for Select Field in Activity by Types Card",
          })}
          labelClassName={styles.filterLabel}
          onChange={handleChangeDateRange}
          options={SIMPLE_DATE_RANGE_LABELS}
          value={dateRange}
        />,
        <Dropdown
          key="menu"
          overlay={
            <Menu>
              <Menu.Item key="activityTypes" onClick={() => showActivityTypesDialog()}>
                {intl.formatMessage({
                  id: "home.cards.activityTypes.dropdown.menuItem.selectActivityTypes",
                  defaultMessage: "Select Activity Types...",
                  description: "Title for the select activity types menu item",
                })}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <MoreButton />
        </Dropdown>,
      ]}
      className={className}
      title={intl.formatMessage({
        id: "home.cards.activityTypes.title",
        defaultMessage: "Activities by Type",
        description: "Activity by Type Card Title",
      })}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && !hasFilteredActivities && !hasActivities && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.activityTypes.cardCta.addActivity",
            defaultMessage: "Add Activity",
            description: "Activity by Types Card CTA button label",
          })}
          buttonVisible={!isReadOnlyMember}
          onClick={handleAddActivity}
          text={intl.formatMessage({
            id: "home.cards.activityTypes.cta.noActivities",
            defaultMessage: "Add your first activity",
            description: "Activity by Types Card CTA text for add your first activity",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050260013-Activities-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.activityTypes.cta.learnMore"
              defaultMessage="Learn more about activities"
              description="Activity by Types Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      {!isLoading && !hasFilteredActivities && hasActivities && (
        <CardCta
          buttonLabel={intl.formatMessage({
            id: "home.cards.activityTypes.cardCta.addActivity",
            defaultMessage: "Add Activity",
            description: "Activity by Types Card CTA button label",
          })}
          buttonVisible={!isReadOnlyMember}
          onClick={handleAddActivity}
          text={intl.formatMessage({
            id: "home.cards.activityTypes.cta.noActivitiesFound",
            defaultMessage: "No activities found. Add an activity or change filters.",
            description: "Activity by Types Card CTA text for add your first activity",
          })}
        >
          <a
            href="https://support.mapmycustomers.com/hc/en-us/articles/360050260013-Activities-Overview"
            rel="noopener noreferrer"
            target="_blank"
          >
            <FormattedMessage
              id="home.cards.activityTypes.cta.learnMore"
              defaultMessage="Learn more about activities"
              description="Activity by Types Card CTA text for learn more link"
            />
          </a>
        </CardCta>
      )}
      {!isLoading && hasFilteredActivities && (
        <HighchartsReact
          containerProps={{ style: chartStyles }}
          highcharts={Highcharts}
          isPureConfig
          options={options}
        />
      )}
      <SelectActivityTypesModal
        entities={activityTypes}
        getDisplayName={defaultNodeNameGetter}
        getKey={defaultIdGetter}
        onConfirm={setActivityTypes}
        onHide={hideActivityTypesDialog}
        selectedEntities={selectedActivityTypes}
        title={intl.formatMessage({
          id: "home.cards.activityTypes.selectActivityTypesDialog.title",
          defaultMessage: "Select Activity Types",
          description: "Title for select activity types dialog",
        })}
        visible={activityTypesDialogVisible}
      />
      {activityListModalVisible && selectedActivityType?.id && (
        <RecordListByActivityTypeModal
          dateRange={dateRange}
          onHide={() => hideActivityListModal()}
          pageSize={25}
          selectedActivityTypeId={selectedActivityType.id}
          title={title}
          users={selectedUsers}
        />
      )}
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  activityTypesWithCount: getActitivityTypesWithCount(state),
  // TODO: fetch count of activities later
  hasActivities: true, // getHasActivities(state),
  isLoading: isActivityByTypesDataLoading(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  fetchActivityByTypesData: fetchActivityByTypesData.request,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTypesCard);
